import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Bot,
  Workflow,
  Globe,
  PhoneCall,
  BarChart3,
  GraduationCap,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const SERVICES = [
  {
    icon: Bot,
    title: "KI-Chatbots & Assistenten",
    text: "Beantworten Anfragen rund um die Uhr — auf Ihrer Webseite, per WhatsApp oder E-Mail. Freundlich, präzise, in Ihrem Ton.",
  },
  {
    icon: Workflow,
    title: "Prozess-Automatisierung",
    text: "Wiederkehrende Abläufe laufen von selbst: Terminbestätigungen, Rechnungen, Erinnerungen — zuverlässig im Hintergrund.",
  },
  {
    icon: PhoneCall,
    title: "Telefon- & Termin-KI",
    text: "Eine freundliche Stimme nimmt Anrufe entgegen, bucht Termine und leitet wichtige Anliegen direkt an Ihr Team weiter.",
  },
  {
    icon: Globe,
    title: "Webseiten & Online-Auftritt",
    text: "Moderne, schnelle Webseiten, die Anfragen bringen — nicht nur gut aussehen. Inklusive Pflege und Weiterentwicklung.",
  },
  {
    icon: BarChart3,
    title: "Daten & Auswertungen",
    text: "Verstehen, was in Ihrem Betrieb wirklich passiert — mit klaren Dashboards statt unübersichtlicher Excel-Listen.",
  },
  {
    icon: GraduationCap,
    title: "Workshops & Schulungen",
    text: "Ihr Team lernt, KI sicher und sinnvoll im Alltag einzusetzen — praxisnah, verständlich, ohne Fachchinesisch.",
  },
];

export const Capabilities = () => {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    const mm = gsap.matchMedia();

    // Desktop: pinned horizontal gallery with wave motion
    mm.add("(min-width: 1024px) and (prefers-reduced-motion: no-preference)", () => {
      const track = trackRef.current;
      const getDistance = () => Math.max(0, track.scrollWidth - window.innerWidth + 160);

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: () => "+=" + (getDistance() + 400),
          pin: true,
          scrub: 0.6,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });
      tl.to(track, { x: () => -getDistance(), ease: "none" }, 0);

      gsap.utils.toArray(".cap-card").forEach((card, i) => {
        tl.fromTo(
          card,
          { y: i % 2 ? 56 : -28 },
          { y: i % 2 ? -28 : 56, ease: "none" },
          0
        );
      });

      gsap.fromTo(
        ".cap-header > *",
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: { trigger: sectionRef.current, start: "top 70%" },
        }
      );
    });

    // Mobile / reduced motion: vertical stagger grid
    mm.add("(max-width: 1023px), (prefers-reduced-motion: reduce)", () => {
      gsap.fromTo(
        ".cap-card",
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.1,
          scrollTrigger: { trigger: trackRef.current, start: "top 85%" },
        }
      );
      gsap.fromTo(
        ".cap-header > *",
        { y: 26, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.7,
          ease: "power3.out",
          stagger: 0.1,
          scrollTrigger: { trigger: ".cap-header", start: "top 88%" },
        }
      );
    });

    return () => mm.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="leistungen"
      data-section-idx="1"
      className="bg-greige relative overflow-hidden"
      data-testid="capabilities-section"
    >
      <div className="mesh-overlay" />
      <div className="relative flex min-h-screen flex-col justify-center py-24 lg:h-screen lg:py-0">
        <div className="cap-header mx-auto w-full max-w-7xl px-6 lg:px-10">
          <p className="eyebrow mb-6 text-[#6E685F]">002 / 006 — Leistungen</p>
          <h2 className="font-display text-4xl text-[#2B2825] sm:text-5xl lg:text-6xl">
            Was wir für Sie bauen
          </h2>
          <p className="mt-5 max-w-xl text-base text-[#6E685F] md:text-lg">
            Sechs Bausteine, ein Ziel: weniger Verwaltungsaufwand, mehr Zeit
            für Ihre Patienten, Mandanten und Kunden.
          </p>
        </div>

        <div
          ref={trackRef}
          className="cap-track mt-12 grid gap-8 px-6 sm:grid-cols-2 lg:mt-14 lg:flex lg:w-max lg:flex-nowrap lg:gap-10 lg:px-24"
        >
          {SERVICES.map((service, i) => (
            <article
              key={service.title}
              className="cap-card neu-card p-8 lg:w-[400px] lg:flex-shrink-0 lg:p-10"
              data-testid={`capability-card-${i}`}
            >
              <div className="cap-icon-wrap mb-8">
                <span className="cap-icon-glow" />
                <service.icon size={28} strokeWidth={1.6} className="relative text-[#2B2825]" />
              </div>
              <h3 className="font-display text-xl text-[#2B2825] sm:text-2xl">
                {service.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-[#6E685F] sm:text-base">
                {service.text}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
